import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { User } from "@shared/models/auth";

async function fetchUser(): Promise<User | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

async function logout(): Promise<void> {
  window.location.href = "/api/logout";
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: user, isLoading, refetch } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    // One retry with a short delay. On mobile, the session cookie can be
    // transiently absent on the first XHR right after an OIDC redirect (iOS
    // Safari ITP / cookie-delivery timing). Without a retry, a single 401
    // permanently sets user=null for this page load, then LoginRedirect sends
    // to /login, which succeeds its own fetch (cookie arrived), bounces back
    // to the workspace, which 401s again — infinite flash loop.
    retry: 1,
    retryDelay: 800,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
    },
  });

  const refetchUser = () => {
    refetch();
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
    refetchUser,
  };
}
